import Api from '../../../../core/services/api/api';
import type {AddCrossSalePartParams} from './add-part';
import type {
    QuickOrderSuggestiveSellingTarget,
    QuoteSuggestiveSellingTarget,
    ShoppingCartSuggestiveSellingTarget,
    SuggestiveSellingTarget,
} from '../types';

/**
 * Commits all staged cross-sale lines to the active target in one or few API calls.
 */
export async function addCrossSalePartsBatch(
    target: SuggestiveSellingTarget,
    lines: AddCrossSalePartParams[],
): Promise<boolean> {
    if (!lines.length) {
        return true;
    }

    switch (target.type) {
        case 'quote':
            return addBatchToQuote(target, lines);
        case 'shoppingCart':
            return addBatchToShoppingCart(target, lines);
        case 'quickOrder':
            return addBatchToQuickOrderDraft(target, lines);
        default:
            return false;
    }
}

async function addBatchToQuote(
    target: QuoteSuggestiveSellingTarget,
    lines: AddCrossSalePartParams[],
): Promise<boolean> {
    const {allowDuplicatePartNumbersInOrders, manageWarehousesInQuote} = target.salesOptions;
    const orderItems = target.getOrderItems();

    const forApi = {
        customerId: target.customerId,
        orderId: target.orderId,
        PartCountChangedList: lines.map(({partNumber, quantity, warehouseCode}) => {
            const existingItem = orderItems.find((e) => e.partNo === partNumber);
            const existingItemQty = Number(existingItem?.orderQty ?? 0);
            return {
                partNo: partNumber,
                finalCount: allowDuplicatePartNumbersInOrders
                    ? quantity
                    : existingItemQty + quantity,
                unitPrice: allowDuplicatePartNumbersInOrders ? null : existingItem?.unitPriceBeforeDiscount,
                ...(manageWarehousesInQuote && {
                    whs: warehouseCode ?? target.warehouseCode,
                }),
                partDescription: allowDuplicatePartNumbersInOrders ? null : existingItem?.description,
            };
        }),
    };

    const response = await Api.updateQuoteItems(forApi, undefined, true);
    if (response?.resultFlag) {
        target.onOrderUpdated(response.data);
        return true;
    }
    return false;
}

async function addBatchToShoppingCart(
    target: ShoppingCartSuggestiveSellingTarget,
    lines: AddCrossSalePartParams[],
): Promise<boolean> {
    const forApi = {
        userId: target.userId,
        PartCountChangedList: lines.map(({partNumber, quantity}) => ({
            partNo: partNumber,
            finalCount: quantity,
        })),
    };
    const response = await Api.updateShoppingCartItems(forApi);
    if (response?.resultFlag) {
        target.onCartUpdated(response.data);
        return true;
    }
    return false;
}

async function addBatchToQuickOrderDraft(
    target: QuickOrderSuggestiveSellingTarget,
    lines: AddCrossSalePartParams[],
): Promise<boolean> {
    for (const line of lines) {
        target.onDraftItemAdded({
            partId: line.partId,
            partNumber: line.partNumber,
            qty: line.quantity,
            warehouseCode: line.warehouseCode ?? target.defaultWarehouseCode,
        });
    }
    return true;
}
